import { DOCUMENT } from '@angular/common';
import { inject, Injectable, signal } from '@angular/core';

export const FONT_SIZE_STORAGE_KEY = 'fontSizePreference';
export const FONT_SIZE_ROOT_PROPERTY = '--font-size-root';

export const FONT_SIZE_PREFERENCES = {
  NORMAL: 'normal',
  SMALL: 'small',
} as const;

export type FontSizePreference = (typeof FONT_SIZE_PREFERENCES)[keyof typeof FONT_SIZE_PREFERENCES];

const FONT_SIZE_ROOT_VALUES: Record<FontSizePreference, string> = {
  [FONT_SIZE_PREFERENCES.NORMAL]: '100%',
  [FONT_SIZE_PREFERENCES.SMALL]: '75%',
};

@Injectable({ providedIn: 'root' })
export class FontSizePreferenceService {
  private readonly document = inject(DOCUMENT);
  private readonly currentPreference = signal<FontSizePreference>(FONT_SIZE_PREFERENCES.NORMAL);

  readonly preference = this.currentPreference.asReadonly();

  initialize(): void {
    const storedPreference = this.readPreference();

    this.currentPreference.set(storedPreference);
    this.applyPreference(storedPreference);
  }

  setPreference(preference: unknown): void {
    const nextPreference = this.normalizePreference(preference);

    this.currentPreference.set(nextPreference);
    this.applyPreference(nextPreference);
    this.persistPreference(nextPreference);
  }

  private readPreference(): FontSizePreference {
    try {
      return this.normalizePreference(this.document.defaultView?.localStorage.getItem(FONT_SIZE_STORAGE_KEY));
    } catch {
      return FONT_SIZE_PREFERENCES.NORMAL;
    }
  }

  private persistPreference(preference: FontSizePreference): void {
    try {
      this.document.defaultView?.localStorage.setItem(FONT_SIZE_STORAGE_KEY, preference);
    } catch {
      return;
    }
  }

  private applyPreference(preference: FontSizePreference): void {
    this.document.documentElement.style.setProperty(FONT_SIZE_ROOT_PROPERTY, FONT_SIZE_ROOT_VALUES[preference]);
  }

  private normalizePreference(preference: unknown): FontSizePreference {
    return preference === FONT_SIZE_PREFERENCES.SMALL ? FONT_SIZE_PREFERENCES.SMALL : FONT_SIZE_PREFERENCES.NORMAL;
  }
}
